/**
 * @file BlogCTA.tsx
 * @summary Composant blog
 *
 * Brique réutilisable pour les articles de blog (CTA, header, layout…). Style éditorial cohérent avec le design system Chistera.
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { Link } from "react-router-dom";
import { CalendarCheck, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

const PHONE = "0624357716";

interface Props {
  variant?: "default" | "hero";
  primaryLabel?: string;
  single?: boolean;
}

export default function BlogCTA({ variant = "default", primaryLabel = "Réserver ma séance d'essai", single = false }: Props) {
  const align = variant === "hero" ? "justify-start" : "justify-center";
  if (single) {
    return (
      <div className={`my-8 flex ${align}`}>
        <Button asChild size="lg" className="bg-cta text-cta-foreground hover:bg-cta/90 font-body normal-case">
          <Link to="/#contact">
            <CalendarCheck className="w-4 h-4" /> {primaryLabel}
          </Link>
        </Button>
      </div>
    );
  }
  return (
    <div className={`flex flex-col sm:flex-row gap-3 ${align}`}>
      <Button asChild size="lg" className="bg-cta text-cta-foreground hover:bg-cta/90 font-body normal-case">
        <Link to="/#contact">
          <CalendarCheck className="w-4 h-4" /> {primaryLabel}
        </Link>
      </Button>
      <Button asChild size="lg" variant="outline" className="border-gold text-gold hover:bg-gold hover:text-gold-foreground font-body normal-case">
        <a href={`tel:${PHONE}`}>
          <Phone className="w-4 h-4" /> Appeler le coach
        </a>
      </Button>
    </div>
  );
}

export function BlogStickyCTA({ label = "Réserver ma séance d'essai" }: { label?: string }) {
  return (
    <>
      {/* Mobile : barre pleine largeur en bas */}
      <div className="md:hidden fixed bottom-4 left-4 right-20 z-40">
        <Link
          to="/#contact"
          className="flex items-center justify-center gap-2 w-full bg-cta text-cta-foreground rounded-full py-3 px-5 font-body normal-case font-semibold shadow-lg shadow-black/40"
        >
          <CalendarCheck className="w-4 h-4" /> {label}
        </Link>
      </div>
      {/* Desktop : pastille flottante bas-droite, toujours visible */}
      <Link
        to="/#contact"
        aria-label={label}
        className="hidden md:inline-flex fixed bottom-6 right-6 z-40 items-center gap-2 bg-cta text-cta-foreground rounded-full py-3 px-5 font-body normal-case font-semibold shadow-lg shadow-black/40 hover:scale-105 transition-transform"
      >
        <CalendarCheck className="w-4 h-4" /> {label}
      </Link>
    </>
  );
}
